<template>
	<div class="editor-webpage">
		<!-- Editor top bar -->
		<div v-if="!previewMode" class="topbar">
			<app-row no-padding>
				<app-cell>
					<app-button
						flat
						no-margin
						icon-only
						icon="fa-chevron-left"
						color="primary"
						@click="updated ? dialogs.closing.show = true : onClose()"
					/>
					<div class="title ml-2 mt-1 d-inline-block">{{ page.name }}</div>
					<div class="subtitle ml-2 mt-1 d-inline-block">/{{ page.url }}</div>
				</app-cell>
				<app-cell class="text-center">
					<input-button-group v-model="screenFormat" color="primary" tile mandatory>
						<app-button icon="fa-desktop" no-margin icon-only max-height="44" />
						<app-button icon="fa-tablet" no-margin icon-only max-height="44" />
						<app-button icon="fa-mobile" no-margin icon-only max-height="44" />
					</input-button-group>
				</app-cell>
				<app-cell class="text-right">
					<span class="mr-2 topbar-state">{{ state }}</span>
					<app-button
						no-margin
						icon-only
						small
						flat
						:disabled="historyCursor >= history.length - 1"
						icon="fa-undo"
						@click="onUndo"
					/>
					<app-button
						no-margin
						icon-only
						small
						flat
						:disabled="historyCursor === 0"
						icon="fa-redo"
						@click="onRedo"
					/>
					<hr class="topbar-divider" />
					<app-button
						color="primary"
						small
						:disabled="!updated"
						@click="onSave"
					>Save</app-button>
					<app-button-confirm
						v-if="hasWarnings"
						color="warning"
						small
						:disabled="!updated && !canPublish"
						icon="fa-exclamation-triangle"
						label-confirm="Publish"
						title="Publish Page"
						custom
						max-width="500"
						@click="onPublish"
					>
						{{ updated ? 'Save & Publish' : 'Publish' }}
						<template v-slot:body>
							<app-dialog-body>
								<div>You are about to publish a page with unresolved warnings.</div>
								<div>Please review the following warnings before publishing:</div>
								<ul>
									<li
										v-for="warning in warnings"
										:key="`warning-${warning.key}`"
									>
										<b>{{warning.key}}</b> {{warning.value}}
									</li>
								</ul>
							</app-dialog-body>
						</template>
					</app-button-confirm>
					<app-button
						v-else
						color="success"
						small
						:disabled="!updated && !canPublish"
						@click="onPublish"
					>
						{{ updated ? 'Save & Publish' : 'Publish' }}
					</app-button>
					<hr class="topbar-divider" />
					<app-button
						color="primary"
						small
						icon="fa-eye"
						@click="onPreview"
					>Preview</app-button>
				</app-cell>
			</app-row>
		</div>

		<!-- Editor Preview bar -->
		<div v-else class="previewbar">
			<app-row>
				<app-cell align-self="center">Preview Mode</app-cell>
				<app-cell class="text-right">
					<app-button
						color="primary"
						small
						flat
						@click="onQuitPreview"
					>Exit</app-button>
				</app-cell>
			</app-row>
		</div>

		<!-- Left bar -->
		<editor-leftbar v-if="!previewMode" v-model="selectedLeftbarItem" :items="leftbarItems">
			<template v-slot:layout>
				<input-select
					v-model="page.layout"
					:items="layouts"
					:disabled="!layoutsLoaded"
					label="Layout"
					@change="onLayoutChange"
				/>
			</template>
			<template v-slot:season>
				<input-select
					v-model="season"
					:items="seasons"
					label="Season"
					@change="onSeasonChange"
				/>
			</template>
			<template v-slot:language>
				<input-select
					v-model="selectedLang"
					:items="languages"
					label="Language"
					@change="onLanguageChange"
				/>
			</template>
			<template v-slot:seo>
				<input-select
					v-model="meta.lang"
					:items="languages"
				/>
				<input-textarea
					v-model="meta.title[meta.lang]"
					label="Title"
					single-line
					:disabled="!meta.init"
					counter="60"
					@change="saveMeta('title')"
				/>
				<input-textarea
					v-model="meta.description[meta.lang]"
					label="Description"
					:disabled="!meta.init"
					counter="160"
					@change="saveMeta('description')"
				/>
			</template>
			<template v-slot:css>
				<input-code-editor
					class="css-editor"
					v-model="page.stylesheet"
					label="Page CSS"
					mode="css"
					@change="onStylesheetChange"
				/>
				<div class="caption mt-2">
					Add custom CSS styles for this page. CSS will be automatically sanitized on save.
				</div>
			</template>
			<template v-slot:assistant>
				<website-page-agent-assistant
					v-if="init"
					:ravello-helper="$ravello"
					:get-editor-api="websitePageGetEditorApi"
					:page-id="pageId"
				/>
			</template>
		</editor-leftbar>

		<!-- Editor frame to remote builder -->
		<iframe
			:class="iframeClass"
			:src="src"
		>
		</iframe>

		<!-- Text edition dialog -->
		<app-dialog
			v-model="dialogs.text.show"
			max-width="800"
			persistent
			:title="`Update ${dialogs.text.extra.href ? 'Action' : 'Text'}: ${dialogs.text.key}`"
		>
			<div
				v-if="hasWarning(dialogs.text.key)"
				class="warning white--text pa-2"
			>
				<app-icon icon="fa-exclamation-triangle mr-2" color="white" />
				<span class="text-caption">{{ page.warnings[dialogs.text.key] }}</span>
			</div>
			<app-dialog-body>
				<input-select
					v-model="dialogs.text.lang"
					:items="languages"
				/>
				<!-- When dialogs is hidden, prevent background rendering of the rich text editor -->
				<input-wysiwyg
					v-if="dialogs.text.extra.rich && dialogs.text.show"
					v-model="dialogs.text.value[dialogs.text.lang]"
					label="Text"
				/>
				<input-text
					v-else
					v-model="dialogs.text.value[dialogs.text.lang]"
					label="Text"
				/>
				<input-text
					v-if="dialogs.text.extra.href"
					v-model="dialogs.text.href[dialogs.text.lang]"
					label="Link"
				/>
				<input-icon
					v-if="dialogs.text.extra.icon"
					v-model="dialogs.text.icon"
					label="Icon"
					icon-set="mdi"
					clearable
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button color="primary" @click="saveText">Save</app-button>
				<app-button @click="dialogs.text.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>

		<!-- Image edition dialog -->
		<app-dialog
			v-model="dialogs.image.show"
			max-width="650"
			persistent
			:title="`Update Image: ${dialogs.image.key}`"
		>
			<app-dialog-body>
				<app-row no-padding>
					<app-cell cols="12" sm="6">
						<input-file
							type="image"
							label="Image"
							image
							image-width="300"
							image-height="300"
							v-if="dialogs.image.show"
							@load="onImageImport"
						/>
					</app-cell>
					<app-cell cols="12" sm="6">
						<input-file
							type="image"
							label="Winter Image"
							image
							image-width="300"
							image-height="300"
							v-if="dialogs.image.show"
							@load="onWinterImageImport"
						/>
					</app-cell>
				</app-row>

				<input-select
					v-model="dialogs.image.lang"
					:items="languages"
				/>
				<input-text
					v-model="dialogs.image.alt[dialogs.image.lang]"
					label="Alt"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					@click="saveImage"
				>Save</app-button>
				<app-button @click="dialogs.image.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>

		<!-- Embedded edition dialog -->
		<app-dialog
			v-model="dialogs.embedded.show"
			max-width="500"
			persistent
			:title="`Update Embedded: ${dialogs.embedded.key}`"
		>
			<app-dialog-body>
				<input-text
					v-model="dialogs.embedded.value"
					label="Embed code"
					:errors="dialogs.embedded.errors"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					:disabled="!dialogs.embedded.value"
					@click="saveEmbedded"
				>Save</app-button>
				<app-button @click="dialogs.embedded.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>

		<!-- Listings edition dialog -->
		<app-dialog
			v-model="dialogs.listings.show"
			max-width="500"
			persistent
			:title="`Update List: ${dialogs.listings.key}`"
		>
			<app-dialog-body>
				<input-switch
					v-if="dialogs.listings.extra.luxury"
					v-model="dialogs.listings.luxury"
					label="Show luxury listings only?"
				/>
				<input-select
					v-if="dialogs.listings.extra.cols"
					v-model="dialogs.listings.cols"
					:items="dialogs.listings.colsOptions"
					label="Columns"
				/>
				<div>Travel Dates</div>
				<app-row>
					<app-cell cols="12" sm="6">
						<input-date-picker
							v-model="dialogs.listings.travelStartDate"
							label="Travel Start Date"
							clearable
						/>
					</app-cell>
					<app-cell cols="12" sm="6">
						<input-date-picker
							v-model="dialogs.listings.travelEndDate"
							label="Travel End Date"
							clearable
						/>
					</app-cell>
				</app-row>
				<!-- @TODO Move this section into a input-tags component -->
				<div class="tags-section">
					<input-chipbox
						v-model="dialogs.listings.value[0]"
						:items="dialogs.listings.tags"
						multiple
						label="Tags (Optional)"
					/>
					<app-row no-padding v-for="(tag, i) in dialogs.listings.value.slice(1)" :key="`tags-${i}`">
						<app-cell cols="12" sm="3" align-self="center">
							<div style="height: 20px;">
								<app-chip
									color="primary"
									clearable
									@click:close="removeListingsAndTags(i+1)"
								>AND</app-chip>
							</div>
						</app-cell>
						<app-cell>
							<input-chipbox
								:items="dialogs.listings.tags"
								v-model="dialogs.listings.value[i + 1]"
								multiple
								placeholder="Tags"
							/>
						</app-cell>
					</app-row>
					<div class="mt-4">
						<app-button color="success" small icon="fa-plus" @click="addListingsAndTags">AND</app-button>
					</div>
				</div>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					@click="saveListingsData"
				>Save</app-button>
				<app-button @click="dialogs.listings.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>

		<!-- Discard changes modal -->
		<app-dialog
			v-model="dialogs.closing.show" max-width="320"
			persistent
			title="Progress will be lost"
		>
			<app-dialog-body>
				Your changes will be lost, are you sure you want to close the editor?
			</app-dialog-body>
			<app-dialog-footer>
				<app-button color="warning" @click="onClose">Close</app-button>
				<app-button @click="dialogs.closing.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</div>
</template>

<script>
import { cloneDeep, mapValues, debounce } from 'lodash'

import locales from '@/datasources/locales'
import md from '@/utils/markdown'
import { dig } from '@/utils/collection'
import Tags from '@/mixins/tags'
import {
	AGENT_HTML_ELASTIC_COMPONENT_ID,
	ELASTIC_INNER_COMPONENT_IDS,
	ELASTIC_SECTION_COMPONENT_IDS,
	normalizeElasticComponentType,
	resolveStoredElasticComponent,
} from '@/features/websitePageAgentTools'
import createWebsitePageEditorApi from '@/features/websitePageEditorApi'
import {
	collectAgentSectionI18nFromPageStrings,
	mergeAgentI18nObjects,
	normalizeAgentHtmlHrefPlaceholders,
	normalizeAgentI18nObject,
	normalizeAgentSectionSlug,
	normalizeAgentSyncSectionRow,
	resolveAgentSectionSlug,
} from '@/utils/websitePageAgentI18n'

import EditorLeftbar from './private/EditorLeftbar.vue'
import WebsitePageAgentAssistant from './WebsitePageAgentAssistant.vue'

const STATES = {
	LOADING: 'Loading...',
	NEW: 'New page',
	NO_CHANGES: 'No changes',
	UNSAVED: 'Unsaved changes',
	SAVED: 'Saved',
}

const ELASTIC_SLUG_REGEXP = /^([a-z]+\.)?[a-z][a-z0-9-_]+$/

export default {
	mixins: [Tags],
	components: { EditorLeftbar, WebsitePageAgentAssistant },
	props: {
		url: {
			type: String,
			required: true,
		},
		websiteId: {
			type: String,
			required: true,
		},
		pageId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			init: false,	// Whether the editor session is initialized
			previewMode: false, // Whether the preview mode is enabled
			selectedLeftbarItem: undefined,	// Selected leftbar item
			state: STATES.LOADING,	// Current state text
			updated: false,	// Whether there's unsaved changes
			canPublish: false, // Whether user can publish the page
			screenFormat: 0,	// Current screen format (desktop,tablet,mobile)
			selectedLang: 'en',
			season: 'summer',

			// Page core data
			page: {
				layout: null,
				fixed: false,
				stylesheet: '',
			},
			/* Page meta data */
			meta: {
				title: {},
				description: {},
				lang: 'en',
				init: false,
			},
			layouts: [],	// Layouts received from the external builder
			locales: [],	// Supported website locales
			warnings: [],	// Active warnings

			history: [],	// History since last save
			historyCursor: 0,	// History cursor
			historyLinkedState: STATES.LOADING,	// Initial history state

			supportedActions: [	// Supported actions from the external builder
				'putLayouts',
				'editContent',
			],
			seasons: [{
				text: 'Summer',
				value: 'summer',
			}, {
				text: 'Winter',
				value: 'winter',
			}],
			layoutsLoaded: false, // Whether the layouts has been loaded
			categoriesMap: {},	// String categories map
			categoriesLoaded: false,	// Whether string categories are loaded

			/** Hub website primary/secondary (for agent context). Set in initEditor. */
			websiteThemeColors: {
				primaryColor: '',
				secondaryColor: '',
			},

			socket: null,	// Websocket client instance
			token: null,	// Editor session token
			debouncedStylesheetEmit: null,	// Debounced function for stylesheet websocket emit

			/* Dialogs states */
			dialogs: {
				text: {
					show: false,
					value: {},
					key: null,
					extra: {
						rich: false,
						href: false,
						icon: false,
					},
					href: {},
					icon: null,
					lang: 'en',
				},
				image: {
					show: false,
					value: null,
					winter: null,
					key: null,
					alt: {},
					lang: 'en',
				},
				embedded: {
					show: false,
					value: null,
					key: null,
					errors: [],
				},
				listings: {
					show: false,
					value: [[]],
					luxury: false,
					cols: 1,
					key: null,
					extra: {
						luxury: false,
						cols: 1,
					},
					tags: [],
					colsOptions: [{
						text: '1 Column per row',
						value: 1,
					}, {
						text: '2 Columns per row',
						value: 2,
					}, {
						text: '3 Columns per row',
						value: 3,
					}],
					travelStartDate: null,
					travelEndDate: null,
					init: false,
				},
				closing: {
					show: false,
				},
			},
		}
	},
	computed: {
		// Iframe remote source
		src() {
			if (!this.init) { return null }
			const [defaultLang] = this.locales
			const localizedUrl = defaultLang !== this.selectedLang ? this.url.replace('builder', `${this.selectedLang}/builder`) : this.url
			return `${localizedUrl}?token=${encodeURIComponent(this.token)}`
				+ `&origin=${encodeURIComponent(`${this.$ravello.constructor.baseUrl}`)}`
		},

		// Current iframe class
		iframeClass() {
			const classes = ['page-frame']

			switch (this.screenFormat) {
			case 1:
				classes.push('tablet')
				break
			case 2:
				classes.push('mobile')
				break
			default:
			}

			if (this.previewMode) {
				classes.push('preview')
			}

			if (this.selectedLeftbarItem >= 0) {
				classes.push('leftbar-open')
			}

			return classes.join(' ')
		},

		// Supported languages
		languages() {
			return this.locales.map(locale => ({
				text: locales[locale].name,
				value: locale,
			}))
		},

		// Is the page has warnings
		hasWarnings() {
			return this.warnings.length
		},

		// Items shown in the left toolbar
		leftbarItems() {
			return [{
				text: 'Layout',
				icon: 'fa-layer-group',
				value: 'layout',
				custom: true,
			}, {
				text: 'Season',
				icon: this.season === 'winter' ? 'fa-snowflake' : 'fa-sun',
				value: 'season',
			}, {
				text: 'Language',
				icon: 'fa-language',
				value: 'language',
			}, {
				text: 'SEO',
				value: 'seo',
				icon: 'fa-search',
			}, {
				text: 'CSS',
				value: 'css',
				icon: 'fa-code',
			}, {
				text: 'Assistant',
				value: 'assistant',
				icon: 'mdi mdi-creation',
			}].filter(item => !this.page.fixed || !item.custom)
		},

		websitePageGetEditorApi() {
			return () => (this.init ? createWebsitePageEditorApi(this) : null)
		},
	},

	async mounted() {
		await this.initEditor()

		this.updateWarnings()
		this.establishSocketCommunication()

		// Initialize debounced stylesheet emit function
		this.debouncedStylesheetEmit = debounce((stylesheet) => {
			if (this.socket) {
				this.socket.emit('action', 'putStylesheet', stylesheet || '')
			}
		}, 500) // 500ms debounce delay
	},

	watch: {
		/**
		 * Initialize meta data if the seo tab is opened for the first time in the left toolbar
		 * @param {number} value - Selected left toolbar item
		 */
		selectedLeftbarItem(value) {
			if (this.leftbarItems[value] && this.leftbarItems[value].value === 'seo') {
				this.initMeta()
			}
		},
	},
	methods: {
		/**
		 * Initialize editor
		 */
		async initEditor() {
			const [page, website] = await Promise.all([
				this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('pages')
					.to(this.pageId)
					.get(),
				this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.field('locales')
					.field('primary_color')
					.field('secondary_color')
					.get(),
			])

			this.locales = website.locales || []
			this.websiteThemeColors = {
				primaryColor: website.primary_color != null ? String(website.primary_color).trim() : '',
				secondaryColor: website.secondary_color != null ? String(website.secondary_color).trim() : '',
			}
			this.canPublish = !!page.draft
			this.page = page.draft ? {
				...page,
				...page.draft,
			} : page
			// Ensure stylesheet field exists
			if (!this.page.stylesheet) {
				this.page.stylesheet = ''
			}
			delete this.page.draft
		},

		/**
		 * Establish the socket communication between the editor client and the iframe client
		 */
		establishSocketCommunication() {
			this.socket = this.$ravello.socket('webeditor')
			this.socket.on('init', (token) => {
				this.token = token
				this.init = true
			})
			this.socket.once('handshake', (clientId) => {
				this.socket.emit('ack', clientId, {
					...this.page,
					season: this.season,
				})
				this.state = this.page.layout ? STATES.NO_CHANGES : STATES.NEW
				this.resetHistory()
			})
			this.socket.on('action', (name, ...data) => {
				if (this.supportedActions.indexOf(name) !== -1) {
					this[name](...data)
				} else {
					console.warn(`Action ${name} not supported`)
				}
			})
		},

		/**
		 * Reset the history
		 */
		resetHistory() {
			this.history = [cloneDeep(this.page)]
			this.historyCursor = 0
			this.historyLinkedState = this.state
		},

		/**
		 * Call after updating page configuration or element
		 */
		afterUpdate() {
			this.updated = true
			this.state = STATES.UNSAVED

			if (this.historyCursor > 0) {
				this.history.splice(0, this.historyCursor)
				this.historyCursor = 0
			}
			this.history.unshift(cloneDeep(this.page))
		},

		/**
		 * Clean up history and reset editor states after saving
		 */
		afterSave() {
			this.updated = false
			this.canPublish = true
			this.state = STATES.SAVED

			// Update publish warnings
			this.updateWarnings()

			// Reset the history as some images won't be available anymore
			this.resetHistory()
		},

		/**
		 * Send the selected layout to the remote builder
		 */
		onLayoutChange() {
			this.socket.emit('action', 'putLayout', this.page.layout)
			this.afterUpdate()
		},

		/**
		 * Reset the socket on season change
		 */
		onSeasonChange() {
			this.init = false
			this.socket.disconnect()
			this.establishSocketCommunication()
		},

		/**
		 * Reset the socket on language change
		 */
		onLanguageChange() {
			this.init = false
			this.socket.disconnect()
			this.establishSocketCommunication()
		},

		/**
		 * Send the close event to the editor parent
		 */
		onClose(event) {
			this.$emit('close', event)
		},

		/**
		 * Update the value of the dialog image when an image is uploaded in the dialog interface
		 */
		onImageImport(data) {
			this.dialogs.image.value = data
		},

		/**
		 * Update the value of the dialog winter image when an image is uploaded in the dialog interface
		 */
		onWinterImageImport(data) {
			this.dialogs.image.winter = data
		},

		/**
		 * Set the page configuration and elements using the history cursor
		 */
		setHistoryCursorAsPage() {
			this.page = cloneDeep(this.history[this.historyCursor])
			this.socket.emit('action', 'putPage', this.page)

			if (this.meta.init) {
				this.meta.init = false
				this.initMeta()
			}
		},

		/**
		 * Go one step backward in the page history
		 */
		onUndo() {
			this.historyCursor += 1
			if (this.historyCursor === this.history.length - 1) {
				this.updated = false
				this.state = this.historyLinkedState
			}
			this.setHistoryCursorAsPage()
		},

		/**
		 * Go one step forward in the page history
		 */
		onRedo() {
			this.historyCursor -= 1
			this.updated = true
			this.state = STATES.UNSAVED
			this.setHistoryCursorAsPage()
		},

		/**
		 * Save the current page state
		 */
		async onSave(_, silent = false) {
			if (!silent) {
				this.$store.dispatch('app/startLoading')
			}

			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('pages')
					.to(this.pageId)
					.update({
						draft: {
							version: this.page.version,
							layout: this.page.layout,
							strings: this.page.strings,
							images: this.page.images,
							data: this.page.data,
							deleted: this.page.deleted,
							warnings: this.page.warnings,
							stylesheet: this.page.stylesheet || '',
						},
					})

				this.afterSave()
				if (!silent) {
					this.$notifications.pushAlert('success', 'Page successfully saved')
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			if (!silent) {
				this.$store.dispatch('app/stopLoading')
			}
		},

		/**
		 * Publish the saved page state
		 */
		async onPublish() {
			this.$store.dispatch('app/startLoading')

			if (this.updated) {
				await this.onSave(null, true)
			}

			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('pages')
					.to(this.pageId)
					.to('publish')
					.create()
				this.$notifications.pushAlert('success', 'Page successfully published')
				this.$store.dispatch('app/stopLoading')
				this.$emit('publish', this.pageId)
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		/**
		 * Switch to preview mode
		 */
		onPreview() {
			this.previewMode = true
			this.socket.emit('action', 'setPreviewMode', true)
			setTimeout(() => {
				document.querySelector('html').classList.add('overflow-y-hidden')
			}, 1)
		},

		/**
		 * Switch to edition mode
		 */
		onQuitPreview() {
			this.previewMode = false
			this.socket.emit('action', 'setPreviewMode', false)
		},

		/**
		 * Handle stylesheet changes
		 */
		onStylesheetChange() {
			// Send updated stylesheet via websocket for real-time preview (debounced)
			if (this.debouncedStylesheetEmit) {
				this.debouncedStylesheetEmit(this.page.stylesheet)
			}
			this.afterUpdate()
		},

		/**
		 * Whether the specified key has known warnings
		 * @param {string} - Element/Text key
		 * @return {boolean} - Whether the specified key has known warnings
		 */
		hasWarning(key) {
			return this.page.warnings && this.page.warnings[key]
		},

		/**
		 * Update page warnings
		 */
		updateWarnings() {
			this.warnings = this.page.warnings ? Object.keys(this.page.warnings).map(key => ({
				key,
				value: this.page.warnings[key],
			})) : []
		},

		/**
		 * Set a warning on the specified key and send it to the remote builder
		 * @param {string} - Element/Text key
		 * @param {string} - Warning
		 */
		setWarning(key, warning) {
			if (!this.page.warnings) {
				this.page.warnings = {}
			}
			if (this.page.warnings[key] && !warning) {
				delete this.page.warnings[key]
			} else if (warning) {
				this.page.warnings[key] = warning
			}
			this.socket.emit('action', 'putWarnings', this.page.warnings)
		},

		/**
		 * Get only the slug part of a key, ignoring the category
		 * @param {string} key - Key
		 */
		getSlugFromKey(key) {
			return /\./.test(key) ? key.split('.')[1] : key
		},

		/**
		 * Initialize page data configuration
		 * @param {string} key - Configuration key
		 */
		initPageDataConfig(key) {
			const keyWithoutCategory = this.getSlugFromKey(key)
			if (!this.page.data) {
				this.page.data = {}
			}
			if (!this.page.data[keyWithoutCategory]) {
				this.page.data[keyWithoutCategory] = {}
			}

			return this.page.data[keyWithoutCategory]
		},

		/**
		 * Load string categories map
		 */
		async loadCategoriesMap() {
			const categories = await this.$ravello.graphql('stringCategories')
				.datasource('_id', 'slug')
				.query()
			this.categoriesMap = categories.reduce((acc, category) => ({
				...acc,
				[category.value]: category.text,
			}), {})

			this.categoriesLoaded = true
		},

		/**
		 * Load localized texts from specified string key
		 * @param {string} - String key
		 * @return {object} - Localized texts
		 */
		async loadText(key) {
			let text = {}

			if (this.page.strings) {
				this.locales.forEach((locale) => {
					const localizedText = dig(this.page.strings, `${locale}.${key}`)
					if (localizedText) {
						text[locale] = localizedText
					}
				})
			}

			if (!Object.keys(text).length) {
				const [category, slug] = key.split('.')

				if (!this.categoriesLoaded) {
					await this.loadCategoriesMap()
				}
				const categoryId = this.categoriesMap[category]
				if (!categoryId) {
					throw new Error('Category not found')
				}
				const found = await this.$ravello.graphql('strings')
					.parent(this.$ravello.graphql('website', this.websiteId))
					.fields(['_id', 'text'])
					.filter('namespace', this.page.namespace)
					.filter('category', categoryId)
					.filter('slug', slug)
					.limit(1)
					.query()

				text = found.length ? found[0].text : {}
			}

			this.locales.forEach((locale) => {
				if (!text[locale]) {
					text[locale] = ''
				}
			})

			return text
		},

		/**
		 * Update localized text
		 * @param {string} locale - Locale code
		 * @param {string} category - String category
		 * @param {string} slug - String slug
		 * @param {string} value - Localized text
		 */
		updateText(locale, category, slug, value) {
			if (!this.page.strings) {
				this.page.strings = {}
			}
			if (!this.page.strings[locale]) {
				this.page.strings[locale] = {}
			}
			if (!this.page.strings[locale][category]) {
				this.page.strings[locale][category] = {}
			}

			this.page.strings[locale][category][slug] = value
		},

		/**
		 * Edit localized texts attached to the specified string key
		 * @param {string} key - String key
		 * @param {array} extra - Extra parameters
		 */
		async editText(key, extra) {
			this.$store.dispatch('app/startLoading')

			try {
				const text = await this.loadText(key)
				this.dialogs.text.key = key
				this.dialogs.text.extra.href = extra.includes('href')
				this.dialogs.text.extra.rich = extra.includes('rich')
				this.dialogs.text.extra.icon = extra.includes('icon')
				this.dialogs.text.value = this.dialogs.text.extra.rich
					? mapValues(text, value => md.toHtml(value)) : text

				if (this.dialogs.text.extra.href) {
					const href = await this.loadText(`${key}-href`)
					this.dialogs.text.href = href
				}

				if (this.dialogs.text.extra.icon) {
					const keyWithoutCategory = this.getSlugFromKey(key)
					this.dialogs.text.icon = dig(this.page, `data.${keyWithoutCategory}.icon`)
				}
				this.dialogs.text.lang = this.selectedLang
				this.dialogs.text.show = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		/**
		 * Detect text anomalies like missing text or duplicate localized content
		 * @param {string} srcLocale - Locale code to compare with
		 * @param {string} locale - Current locale code
		 * @param {string} category - String category
		 * @param {string} slug - String slug
		 */
		detectTextAnomalies(srcLocale, locale, category, slug) {
			if (locale !== srcLocale) {
				const normalizedText = this.page.strings[locale][category][slug].replace(/<\/?[^>]+>/g, '')
				if (normalizedText === '') {
					return `${locales[locale].name} text is missing`
				}
				if (normalizedText === this.page.strings[srcLocale][category][slug]
					&& normalizedText.split(' ').length > 3) {
					return `${locales[locale].name} text is the same as the original text`
				}
			}

			return null
		},

		/**
		 * Save localized texts and send the new texts to the remote builder
		 */
		async saveText() {
			this.dialogs.text.show = false
			this.$store.dispatch('app/startLoading')

			const {
				key,
				value,
				extra,
				href: extraHref,
				icon: extraIcon,
			} = this.dialogs.text

			const [category, slug] = key.split('.')
			let warning = null

			this.locales.forEach((locale) => {
				const text = value[locale] || ''
				this.updateText(locale, category, slug, extra.rich ? md.fromHtml(text) : text)

				if (extra.href) {
					const href = extraHref[locale] || ''
					this.updateText(locale, category, `${slug}-href`, href)
				}

				// @todo - Change later, maybe have a preferred language
				if (!warning) {
					warning = this.detectTextAnomalies('en', locale, category, slug)
				}
			})

			this.setWarning(key, warning)
			this.socket.emit('action', 'putLocales', this.page.strings)
			if (category === 'texts') {
				this.refreshAgentSectionI18nCacheForTextSlug(slug)
			}

			if (extra.icon) {
				const data = this.initPageDataConfig(key)
				data.icon = extraIcon
				this.socket.emit('action', 'putData', this.page.data)
			}

			this.$store.dispatch('app/stopLoading')

			this.afterUpdate()
		},

		/**
		 * Initialize meta data for the page
		 */
		async initMeta() {
			const [title, description] = await Promise.all([
				this.loadText('meta.title'),
				this.loadText('meta.description'),
			])
			this.meta.title = title
			this.meta.description = description
			this.meta.init = true
		},

		/**
		 * Save meta data
		 * @param {slug} - Meta data slug
		 */
		async saveMeta(slug) {
			let warning = null

			this.locales.forEach((locale) => {
				this.updateText(locale, 'meta', slug, this.meta[slug][locale] || '')
				if (!warning) {
					warning = this.detectTextAnomalies('en', locale, 'meta', slug)
				}
			})

			this.setWarning(`meta.${slug}`, warning)
			this.afterUpdate()
		},

		/**
		 * Edit image attached to the specified image key
		 * @param {string} key - Image key
		 */
		async editImage(key) {
			this.dialogs.image.show = true
			this.dialogs.image.key = key
			this.dialogs.image.value = null
			this.dialogs.image.winter = null

			this.dialogs.image.lang = this.selectedLang

			const alt = await this.loadText(`alt.${key}`)
			this.dialogs.image.alt = alt
		},

		async uploadDraft(key, value) {
			const draftCandidate = await this.$ravello.request().v(2)
				.to('websites')
				.to(this.websiteId)
				.to('pages')
				.to(this.pageId)
				.to('draftimages')
				.limit(1)
				.param('slug', key)
				.field('slug')
				.get()

			const id = draftCandidate.length && draftCandidate[0]._id

			const request = this.$ravello.request().v(2)
				.to('websites')
				.to(this.websiteId)
				.to('pages')
				.to(this.pageId)
				.to('draftimages')

			const draftImage = id ? await request.to(id).update({
				data: value,
			}) : await request.create({
				slug: key,
				data: value,
			})

			if (!this.page.images) {
				this.page.images = {}
			}
			this.page.images[key] = {
				id: draftImage._id,
				token: draftImage.token,
			}

			this.socket.emit('action', 'putImage', key, {
				...this.page.images[key],
				pending: true,
				ts: Date.now(),
			})
		},

		/**
		 * Save edited image and send the new image to the remote builder
		 */
		async saveImage() {
			this.dialogs.image.show = false
			this.$store.dispatch('app/startLoading')

			const {
				key, value, winter, alt,
			} = this.dialogs.image

			try {
				if (value) {
					await this.uploadDraft(key, value)
				}
				if (winter) {
					await this.uploadDraft(`${key}-season-winter`, winter)
				}

				this.locales.forEach((locale) => {
					const altText = alt[locale] || ''
					this.updateText(locale, 'alt', key, altText)
				})
				this.socket.emit('action', 'putLocales', this.page.strings)

				this.afterUpdate()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		/**
		 * Edit listings grid configurations attached to the specified listings key
		 * @param {string} key - Listings grid key
		 * @param {array} extra - Extra parameters
		 */
		async editListings(key, extra) {
			if (!this.dialogs.listings.init) {
				this.$store.dispatch('app/startLoading')
				this.dialogs.listings.tags = await this.loadWebsiteTags(this.websiteId)
				this.dialogs.listings.init = true
			}

			this.dialogs.listings.key = key
			this.dialogs.listings.extra.luxury = extra.includes('luxury')
			this.dialogs.listings.extra.cols = extra.includes('cols')
			const tags = dig(this.page, `data.${key}.tag`)
			this.dialogs.listings.travelStartDate = dig(this.page, `data.${key}.travelStartDate`)
			this.dialogs.listings.travelEndDate = dig(this.page, `data.${key}.travelEndDate`)

			this.dialogs.listings.value = tags ? tags.split(',').map(tag => tag.split(';')) : [[]]
			if (this.dialogs.listings.extra.luxury) {
				this.dialogs.listings.luxury = dig(this.page, `data.${key}.luxury`)
			}
			if (this.dialogs.listings.extra.cols) {
				this.dialogs.listings.cols = dig(this.page, `data.${key}.cols`) || 1
			}
			this.dialogs.listings.show = true

			this.$store.dispatch('app/stopLoading')
		},

		addListingsAndTags() {
			this.dialogs.listings.value = [...this.dialogs.listings.value, []]
		},

		removeListingsAndTags(index) {
			const softClone = [...this.dialogs.listings.value]
			softClone.splice(index, 1)
			this.dialogs.listings.value = softClone
		},

		/**
		 * Save edited listings grid configs and send the new configurations to the remote builder
		 */
		async saveListingsData() {
			this.dialogs.listings.show = false
			this.$store.dispatch('app/startLoading')

			const {
				key, value, luxury, cols, extra, travelStartDate, travelEndDate,
			} = this.dialogs.listings

			const data = this.initPageDataConfig(key)
			data.travelStartDate = travelStartDate
			data.travelEndDate = travelEndDate
			data.tag = value
				.map(tag => tag.join(';'))
				.join(',')

			if (extra.luxury) {
				data.luxury = luxury
			}
			if (extra.cols) {
				data.cols = cols
			}

			this.socket.emit('action', 'putData', this.page.data)
			this.$store.dispatch('app/stopLoading')

			this.afterUpdate()
		},

		/**
		 * Edit embedded content attached to the embedded key
		 * @param {string} key - Embedded key
		 */
		editEmbedded(key) {
			this.dialogs.embedded.key = key
			const embedData = dig(this.page, `data.${key}.embed`)
			const embedAttrs = embedData ? Object.keys(embedData).reduce((acc, attr) => [...acc,
				`${attr}="${embedData[attr]}"`,
			], []).join(' ') : null
			this.dialogs.embedded.value = embedAttrs ? `<iframe ${embedAttrs}></iframe>` : ''
			this.dialogs.embedded.show = true
			this.dialogs.embedded.errors = []

			this.$store.dispatch('app/stopLoading')
		},

		/**
		 * Save edited embedded content and send the new embedded content to the remote builder
		 */
		saveEmbedded() {
			this.dialogs.embedded.errors = []
			if (!/^<iframe/.test(this.dialogs.embedded.value)) {
				this.dialogs.embedded.errors = ['Invalid embed code']
				return
			}

			this.dialogs.embedded.show = false
			this.$store.dispatch('app/startLoading')
			const matches = /^<iframe ([^\\>]+)/.exec(this.dialogs.embedded.value)
			if (matches && matches[1]) {
				const [, attrs] = matches
				const embedData = attrs.replace(/([^"]+"[^"]+") /g, '$1__SPACE__').split('__SPACE__').reduce((acc, attr) => {
					const [key, ...value] = attr.split('=')
					acc[key] = value.join('=').replace(/^"(.*)"$/g, '$1')
					return acc
				}, {})
				const data = this.initPageDataConfig(this.dialogs.embedded.key)
				data.embed = embedData

				this.socket.emit('action', 'putData', this.page.data)
			}
			this.$store.dispatch('app/stopLoading')

			this.afterUpdate()
		},

		/**
		 * Validate elastic component id
		 */
		validateElasticComponentId(data, configName, id) {
			const idWithoutCategory = this.getSlugFromKey(id)
			if (data[configName].some(key => this.getSlugFromKey(key) === idWithoutCategory)) {
				this.$notifications.pushAlert('error', `This slug already exists: ${idWithoutCategory}`)
				return false
			}

			if (!ELASTIC_SLUG_REGEXP.test(id)) {
				this.$notifications.pushAlert('error', 'Slug can only contains lowercase characters, digits and the -_ symbols')
				return false
			}

			return true
		},

		/** Unique slug for a new elastic section (matches ELASTIC_SLUG_REGEXP). */
		generateElasticSectionId(component) {
			const normalized = normalizeElasticComponentType(component)
			const tail = normalized.replace(/[^a-z0-9-_]/g, '-').replace(/^-+|-+$/g, '') || 'section'
			return `${tail}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
		},

		/**
		 * Add elastic component
		 * @todo - Review as it's hardly configurable (but might not change much for the categories)
		 * @param {object} data - Elastic container data
		 * @param {object} options - Creation options
		 * @param {string} options.id - Specific id (generated when omitted but component is set)
		 * @param {string} options.component - Remote component to set as the elastic component
		 * @param {string} options.category - Component category
		 */
		addElasticComponent(data, configName, options) {
			if (!data[configName]) {
				data[configName] = []
			}

			let { id } = options
			const hasComponent = typeof options.component === 'string' && options.component.trim() !== ''

			let normalizedType = ''
			if (hasComponent) {
				const resolved = resolveStoredElasticComponent(options.component)
				if (!resolved.ok) {
					const allowed = [
						...ELASTIC_SECTION_COMPONENT_IDS,
						...ELASTIC_INNER_COMPONENT_IDS,
					]
					this.$notifications.pushAlert(
						'error',
						`Unknown elastic section type "${options.component}". Use one of: ${allowed.join(', ')}`,
					)
					return
				}
				normalizedType = resolved.component
			}

			// Agent/tools often send { op: 'add', component } without id; previously this pushed a timestamp
			// with no page.data[id] entry, breaking the builder preview.
			if ((id === undefined || id === null || id === '') && hasComponent) {
				id = this.generateElasticSectionId(options.component)
			}
			if (id === undefined || id === null || id === '') {
				id = Date.now()
			}

			if (typeof id === 'string' && !this.validateElasticComponentId(data, configName, id)) {
				return
			}

			data[configName].push(id)

			if (hasComponent) {
				const componentData = this.initPageDataConfig(id)
				componentData.component = normalizedType

				if (options.data && typeof options.data === 'object') {
					Object.keys(options.data).forEach((attr) => {
						componentData[attr] = options.data[attr]
					})
				}
			}
		},

		/**
		 * Swap two elastic components
		 * @param {object} data - Elastic container data
		 * @param {string} configName - Configuration name
		 * @param {object} options - Swap options
		 * @param {string} options.id - Id of the column/row to swap
		 * @param {string|number} options.increment - New position increment/decrement
		 */
		swapElasticComponents(data, configName, options) {
			const { id, increment } = options
			if (!data[configName]) { return }

			const position = data[configName].indexOf(id)
			const newPosition = position + parseInt(increment, 10)
			data[configName][position] = data[configName][newPosition]
			data[configName][newPosition] = id
		},

		/**
		 * Remove an element in the elastic component
		 * @param {string} elementId - Id of the element inside the component
		 */
		removeElasticElement(elementId) {
			const [elementCategory, elementSlug] = elementId.split('.')
			const configId = elementSlug || elementId
			if (this.page.data) {
				delete this.page.data[configId]
			}
			if (this.page.images) {
				delete this.page.images[configId]
			}

			if (elementSlug) {
				this.locales.forEach((locale) => {
					if (this.page.strings && this.page.strings[locale]
						&& this.page.strings[locale][elementCategory]
					) {
						delete this.page.strings[locale][elementCategory][elementSlug]
					}
				})
			}
		},

		/**
		 * Remove elastic component
		 * @param {object} data - Elastic container data
		 * @param {string} configName - Configuration name
		 * @param {object} options - Deletion options
		 * @param {string} options.id - ID of the component to delete
		 * @param {array} options.elements - ID of elements inside the component to delete
		 */
		removeElasticComponent(data, configName, options) {
			const { id, elements } = options

			if (elements) {
				if (!this.page.deleted) {
					this.page.deleted = []
				}
				this.page.deleted = [...this.page.deleted, ...elements]

				elements.forEach((element) => {
					this.removeElasticElement(element)
				})
			}

			if (!id || !data[configName] || !Array.isArray(data[configName])) { return }
			const index = data[configName].indexOf(id)
			if (index === -1) { return }
			data[configName].splice(index, 1)
		},

		/**
		 * Edit elastic container and send the updates to the remote builder
		 * @param {string} key - Elastic container key
		 * @param {string} configName - Configuration name
		 * @param {array} options - Options
		 * @param {string} options.op - Operator name (add|reorder|remove)
		 * @param {object} options.options - Operation options
		 */
		editElasticContainer(key, configName, { op, ...options }) {
			const data = this.initPageDataConfig(key)

			switch (op) {
			case 'add':
				this.addElasticComponent(data, configName, options)
				break
			case 'reorder':
				this.swapElasticComponents(data, configName, options)
				break
			case 'remove':
				this.removeElasticComponent(data, configName, options)
				break
			default:
				return
			}

			this.socket.emit('action', 'putData', this.page.data)
			this.afterUpdate()
		},

		/**
		 * Update page data configuration and send it back to the remote builder
		 * @param {string} key - Configuration key
		 * @param {string} name - Parameter name
		 * @param {*} value - Parameter value
		 */
		updatePageData(key, name, value) {
			const data = this.initPageDataConfig(key)
			data[name] = value
			this.socket.emit('action', 'putData', this.page.data)
			this.afterUpdate()
		},

		async migrateText(id, newId) {
			const text = await this.loadText(id)
			if (!text) { return }
			const [category, slug] = newId.split('.')
			Object.keys(text).forEach((locale) => {
				this.updateText(locale, category, slug, text[locale])
			})

			this.socket.emit('action', 'putLocales', this.page.strings)
		},

		/**
		 * Process migration step
		 */
		async processMigrationStep(op, options) {
			switch (op) {
			case 'layout':
				this.page.layout = options.value
				this.onLayoutChange()
				break
			case 'texts':
				await this.migrateText(options.id, options.newId)
				break
			case 'version':
				this.page.version = options.value
				this.socket.emit('action', 'putPage', this.page)
				this.afterUpdate()
				break
			default:
			}
		},

		/* Supported actions, please ensure the method name is set in the supportedActions */

		/**
		 * Add supported layouts from the remote builder in the editor
		 */
		putLayouts(layouts) {
			this.layouts = layouts
			this.layoutsLoaded = true
		},

		/**
		 * Edit remote content
		 * @param {object} params - Parameters
		 * @param {string} params.type - Element type
		 * @param {string} params.slug - Element slug/key
		 * @param {array} params.extra - Extra parameters
		 * @param {object} options - Edition options
		 */
		editContent({
			type, slug, extra = [], ...options
		}) {
			switch (type) {
			case 'migration':
				this.processMigrationStep(options.op, options)
				break
			case 'text':
				this.editText(slug, extra)
				break
			case 'image':
				this.editImage(slug, extra)
				break
			case 'listings':
				this.editListings(slug, extra)
				break
			case 'embedded':
				this.editEmbedded(slug, extra)
				break
			case 'repeat':
			case 'elastic':
				this.editElasticContainer(slug, type, options)
				break
			// Might deprecate?
			case 'media':
				this.updatePageData(slug, 'media', options.value)
				break
			case 'options':
				this.updatePageData(slug, options.key, options.value)
				break
			default:
				this.$notifications.pushAlert('error', `Unrecognized content type ${type}`)
			}
		},

		/**
		 * Remove page.strings[locale].texts keys for one agent-html section (`agent-<sectionId>-*`).
		 * @param {string} sectionId
		 */
		scrubAgentHtmlStringsForSection(sectionId) {
			const sid = String(sectionId || '').trim()
			if (!sid || !this.page.strings) {
				return
			}
			const prefix = `agent-${sid}-`
			const locs = Array.isArray(this.locales) && this.locales.length
				? this.locales.map(l => String(l))
				: Object.keys(this.page.strings)
			for (let li = 0; li < locs.length; li += 1) {
				const locale = locs[li]
				const texts = dig(this.page.strings, `${locale}.texts`)
				if (!texts || typeof texts !== 'object') {
					// eslint-disable-next-line no-continue
					continue
				}
				Object.keys(texts).forEach((k) => {
					if (k.startsWith(prefix)) {
						delete texts[k]
					}
				})
			}
		},

		/**
		 * Remove draft image keys for one agent-html section (`agent-<sectionId>-*` and winter variants).
		 * @param {string} sectionId
		 */
		scrubAgentHtmlImagesForSection(sectionId) {
			const sid = String(sectionId || '').trim()
			if (!sid || !this.page.images || typeof this.page.images !== 'object') {
				return
			}
			const prefix = `agent-${sid}-`
			const keys = Object.keys(this.page.images).filter(k => k.startsWith(prefix))
			for (let i = 0; i < keys.length; i += 1) {
				const k = keys[i]
				delete this.page.images[k]
				if (this.socket) {
					this.socket.emit('action', 'removeImage', k)
				}
			}
		},

		/**
		 * Keep page.data[sectionId].i18n aligned with page.strings after manual text edits.
		 * @param {string} textSlug - texts category slug (e.g. agent-hero-title)
		 */
		refreshAgentSectionI18nCacheForTextSlug(textSlug) {
			const slug = String(textSlug || '')
			if (!slug.startsWith('agent-') || !this.page.data) {
				return
			}
			const { body } = this.page.data
			const elasticOrder = body && Array.isArray(body.elastic) ? body.elastic : []
			const agentIds = this.getAgentHtmlElasticIds(elasticOrder, this.page.data)
			for (let i = 0; i < agentIds.length; i += 1) {
				const sectionId = agentIds[i]
				const prefix = `agent-${sectionId}-`
				if (!slug.toLowerCase().startsWith(prefix.toLowerCase())) {
					// eslint-disable-next-line no-continue
					continue
				}
				const fromStrings = collectAgentSectionI18nFromPageStrings(
					this.page.strings,
					this.locales,
					sectionId,
				)
				const componentData = this.page.data[sectionId]
				if (!componentData || typeof componentData !== 'object') {
					return
				}
				if (fromStrings) {
					componentData.i18n = cloneDeep(fromStrings)
				} else {
					delete componentData.i18n
				}
				return
			}
		},

		/**
		 * Pick a non-empty string from per-locale values for agent i18n (fill missing website locales).
		 * @param {object} perLoc
		 * @returns {string}
		 */
		agentI18nFallbackValue(perLoc) {
			if (!perLoc || typeof perLoc !== 'object' || Array.isArray(perLoc)) {
				return ''
			}
			const prefer = ['en', 'en-US', 'en_GB']
			for (let pi = 0; pi < prefer.length; pi += 1) {
				const k = prefer[pi]
				if (Object.prototype.hasOwnProperty.call(perLoc, k)) {
					const v = perLoc[k]
					if (v != null && String(v).trim()) {
						return String(v)
					}
				}
			}
			const keys = Object.keys(perLoc)
			for (let i = 0; i < keys.length; i += 1) {
				const v = perLoc[keys[i]]
				if (v != null && String(v).trim()) {
					return String(v)
				}
			}
			return ''
		},

		/**
		 * @param {string} sectionId
		 * @param {object} i18nObj normalized map fieldId -> { locale -> text }
		 */
		applyAgentSectionI18nToPageStrings(sectionId, i18nObj) {
			if (!i18nObj) {
				return
			}
			const sid = String(sectionId || '').trim()
			const siteLocales = Array.isArray(this.locales) && this.locales.length
				? this.locales.map(l => String(l))
				: Object.keys(this.page.strings || {})
			Object.keys(i18nObj).forEach((fieldId) => {
				const perLoc = i18nObj[fieldId]
				if (!perLoc || typeof perLoc !== 'object' || Array.isArray(perLoc)) {
					return
				}
				const slug = `agent-${sid}-${fieldId}`
				const fallback = this.agentI18nFallbackValue(perLoc)
				for (let li = 0; li < siteLocales.length; li += 1) {
					const locale = siteLocales[li]
					let value = ''
					if (Object.prototype.hasOwnProperty.call(perLoc, locale)) {
						value = perLoc[locale] != null ? String(perLoc[locale]) : ''
					} else {
						value = fallback
					}
					this.updateText(locale, 'texts', slug, value)
				}
			})
		},

		/**
		 * Expand a partial `sections` list from the assistant so omitted agent-html blocks are not deleted.
		 * - `[]` is handled by the caller (remove all) — this is not called for an empty list.
		 * - Same multiset as the page → use incoming order (reorder).
		 * - Every incoming id exists on the page but some page ids are missing from incoming → patch + preserve.
		 * - Every page id appears in incoming (possibly plus new ids) → patch + append new.
		 * - Otherwise, **no** incoming id matches an existing agent-html id (all-new slugs): preserve every existing
		 *   block in order, then append each incoming row (typical “only sent the new section” payloads).
		 * - Otherwise (overlap + some existing ids omitted): apply incoming in order, then append preserved rows for
		 *   existing ids not already listed (original order).
		 * Caller may set **`sectionsReplaceAll`** on `syncAgentHtmlLayout` to skip merging and treat `sections` as the
		 * full authoritative agent-html list (e.g. regenerate the whole stack with new slugs).
		 *
		 * @param {Array<object>} incomingSections
		 * @param {string[]} existingAgentIds
		 * @param {object} data - page.data
		 * @returns {Array<object>}
		 */
		mergeAgentHtmlSyncSections(incomingSections, existingAgentIds, data) {
			if (!Array.isArray(incomingSections) || incomingSections.length === 0) {
				return incomingSections
			}
			const incoming = incomingSections.map(r => (
				r && typeof r === 'object' && !Array.isArray(r) ? r : {}
			))
			const existingAgentIdsList = Array.isArray(existingAgentIds)
				? existingAgentIds.map(id => String(id))
				: []
			const existingSet = new Set(existingAgentIdsList)
			const incomingIds = []
			for (let i = 0; i < incoming.length; i += 1) {
				const id = String(incoming[i].id || '').trim()
				if (id) {
					incomingIds.push(id)
				}
			}
			const incomingSet = new Set(incomingIds)

			const sameMultiset = existingAgentIdsList.length === incomingIds.length
				&& existingAgentIdsList.length > 0
				&& incomingIds.length === incomingSet.size
				&& incomingIds.length === existingSet.size
				&& existingAgentIdsList.every(id => incomingSet.has(id))

			if (sameMultiset) {
				return [...incoming]
			}

			let everyIncomingInExisting = true
			for (let i = 0; i < incomingIds.length; i += 1) {
				if (!existingSet.has(incomingIds[i])) {
					everyIncomingInExisting = false
					break
				}
			}
			let everyExistingInIncoming = true
			for (let i = 0; i < existingAgentIdsList.length; i += 1) {
				if (!incomingSet.has(existingAgentIdsList[i])) {
					everyExistingInIncoming = false
					break
				}
			}

			const findIncomingRow = (id) => {
				for (let j = 0; j < incoming.length; j += 1) {
					if (String(incoming[j].id || '').trim() === id) {
						return incoming[j]
					}
				}
				return null
			}

			const preservedRow = (id) => {
				const entry = data[id] && typeof data[id] === 'object' ? data[id] : null
				const html = entry && entry.html != null ? String(entry.html) : ''
				return { id, html }
			}

			if (everyIncomingInExisting && !sameMultiset) {
				const out = []
				for (let i = 0; i < existingAgentIdsList.length; i += 1) {
					const id = existingAgentIdsList[i]
					const row = findIncomingRow(id)
					out.push(row || preservedRow(id))
				}
				return out
			}

			if (everyExistingInIncoming) {
				const out = []
				for (let i = 0; i < existingAgentIdsList.length; i += 1) {
					const id = existingAgentIdsList[i]
					const row = findIncomingRow(id)
					out.push(row || preservedRow(id))
				}
				for (let j = 0; j < incoming.length; j += 1) {
					const rid = String(incoming[j].id || '').trim()
					if (rid && !existingSet.has(rid)) {
						out.push(incoming[j])
					}
				}
				return out
			}

			const hasOverlap = incomingIds.some(id => existingSet.has(id))
			if (!hasOverlap) {
				if (existingAgentIdsList.length === 0) {
					return [...incoming]
				}
				const out = []
				for (let i = 0; i < existingAgentIdsList.length; i += 1) {
					out.push(preservedRow(existingAgentIdsList[i]))
				}
				for (let j = 0; j < incoming.length; j += 1) {
					const rid = String(incoming[j].id || '').trim()
					if (rid && !existingSet.has(rid)) {
						out.push(incoming[j])
					}
				}
				return out
			}
			const out = [...incoming]
			const outIds = new Set(incomingIds)
			for (let i = 0; i < existingAgentIdsList.length; i += 1) {
				const id = existingAgentIdsList[i]
				if (!outIds.has(id)) {
					out.push(preservedRow(id))
					outIds.add(id)
				}
			}
			return out
		},

		/**
		 * Fill missing `html` on a sync row from existing page.data (partial section updates).
		 * Does not add `agentCss` when omitted — apply uses presence of keys for patch semantics.
		 * @param {object} row
		 * @param {object} data - page.data
		 * @returns {object}
		 */
		enrichAgentSectionSyncRow(row, data) {
			const r = row && typeof row === 'object' && !Array.isArray(row) ? row : {}
			const id = String(r.id || '').trim()
			if (!id) {
				return r
			}
			if (Object.prototype.hasOwnProperty.call(r, 'html')) {
				return r
			}
			const entry = data[id] && typeof data[id] === 'object' ? data[id] : null
			const html = entry && entry.html != null ? String(entry.html) : ''
			return { ...r, html }
		},

		/**
		 * Reorder merged section rows using `afterSectionId` hints from the original tool payload (in array order).
		 * @param {Array<object>} mergedRows
		 * @param {Array<object>} placementHints
		 * @returns {Array<object>}
		 */
		applyAgentSectionAfterPlacements(mergedRows, placementHints) {
			if (!Array.isArray(mergedRows) || mergedRows.length === 0) {
				return mergedRows
			}
			const hints = Array.isArray(placementHints)
				? placementHints.filter(h => h && typeof h === 'object' && !Array.isArray(h))
				: []
			if (!hints.length) {
				return mergedRows
			}
			const byId = new Map()
			for (let i = 0; i < mergedRows.length; i += 1) {
				const id = String(mergedRows[i].id || '').trim()
				if (id) {
					byId.set(id, mergedRows[i])
				}
			}
			let order = mergedRows.map(r => String(r.id || '').trim()).filter(Boolean)
			for (let h = 0; h < hints.length; h += 1) {
				const hid = String(hints[h].id || '').trim()
				const after = String(hints[h].afterSectionId || '').trim()
				if (hid && after && byId.has(hid) && order.includes(after)) {
					order = order.filter(x => x !== hid)
					const ai = order.indexOf(after)
					if (ai === -1) {
						order.push(hid)
					} else {
						order.splice(ai + 1, 0, hid)
					}
				}
			}
			return order.map(id => byId.get(id)).filter(Boolean)
		},

		/**
		 * @param {unknown} raw
		 * @returns {{ ok: true, order: string[] } | { ok: false, reason: string }}
		 */
		parseAgentSectionsOrderArg(raw) {
			let order = raw
			if (typeof order === 'string') {
				try {
					order = JSON.parse(order)
				} catch (_e) {
					return { ok: false, reason: 'agent_sections_order_invalid_json' }
				}
			}
			if (!Array.isArray(order)) {
				return { ok: false, reason: 'agent_sections_order_must_be_array' }
			}
			const out = []
			for (let i = 0; i < order.length; i += 1) {
				const id = normalizeAgentSectionSlug(order[i])
				if (!id) {
					return { ok: false, reason: 'agent_sections_order_empty_id', index: i }
				}
				if (!ELASTIC_SLUG_REGEXP.test(id)) {
					return { ok: false, reason: 'invalid_section_slug', id }
				}
				out.push(id)
			}
			if (new Set(out).size !== out.length) {
				return { ok: false, reason: 'duplicate_section_id' }
			}
			return { ok: true, order: out }
		},

		/**
		 * @param {string[]} elasticOrder
		 * @param {object} data
		 * @returns {string[]}
		 */
		getAgentHtmlElasticIds(elasticOrder, data) {
			const eo = Array.isArray(elasticOrder) ? elasticOrder : []
			return eo.filter((id) => {
				const key = String(id)
				const entry = data[key] && typeof data[key] === 'object' ? data[key] : null
				const comp = entry && entry.component
					? normalizeElasticComponentType(entry.component)
					: ''
				return comp === AGENT_HTML_ELASTIC_COMPONENT_ID
			}).map(id => String(id))
		},

		/**
		 * Replace all buildable-agent-html elastic sections with a new ordered list (after non-agent sections),
		 * and/or update page.stylesheet. Used by the in-editor AI assistant.
		 * @param {{
		 *   stylesheet?: string,
		 *   sections?: Array<object>,
		 *   sectionsReplaceAll?: boolean,
		 *   agentSectionsOrder?: string|string[],
		 * }} payload
		 * @returns {{ ok?: boolean, reason?: string, elasticSectionOrder?: string[], agentSectionIds?: string[] }}
		 */
		syncAgentHtmlLayout(payload) {
			const p = payload || {}
			const hasSections = Object.prototype.hasOwnProperty.call(p, 'sections')
			const hasStylesheet = Object.prototype.hasOwnProperty.call(p, 'stylesheet')
			const hasAgentSectionsOrder = Object.prototype.hasOwnProperty.call(p, 'agentSectionsOrder')

			if (!hasSections && !hasStylesheet && !hasAgentSectionsOrder) {
				return { ok: false, reason: 'nothing_to_update' }
			}

			let agentSectionIds = null
			let elasticOrderOut = null
			let didDataOrStrings = false

			if (!this.page.data) {
				this.page.data = {}
			}
			const { data } = this.page
			const body = this.initPageDataConfig('body')
			if (!Array.isArray(body.elastic)) {
				body.elastic = []
			}

			const existingOrder = body.elastic.map(id => String(id))
			const nonAgentIds = existingOrder.filter((id) => {
				const entry = data[id]
				const comp = entry && entry.component
					? normalizeElasticComponentType(entry.component)
					: ''
				return comp !== AGENT_HTML_ELASTIC_COMPONENT_ID
			})

			const applyAgentOrderTail = (agentIdOrder) => {
				const unique = Array.isArray(agentIdOrder) ? agentIdOrder.map(id => String(id).trim()).filter(Boolean) : []
				body.elastic = [...nonAgentIds, ...unique]
				elasticOrderOut = [...body.elastic]
				agentSectionIds = [...unique]
			}

			const validateAndApplyAgentSectionsOrder = (desiredOrder) => {
				const parsed = this.parseAgentSectionsOrderArg(desiredOrder)
				if (!parsed.ok) {
					return parsed
				}
				const { order } = parsed
				const currentAgent = this.getAgentHtmlElasticIds(body.elastic, data)
				if (order.length !== currentAgent.length) {
					return { ok: false, reason: 'agent_sections_order_length_mismatch' }
				}
				const curSet = new Set(currentAgent)
				for (let i = 0; i < order.length; i += 1) {
					if (!curSet.has(order[i])) {
						return { ok: false, reason: 'agent_sections_order_unknown_id', id: order[i] }
					}
				}
				applyAgentOrderTail(order)
				return { ok: true }
			}

			if (hasSections) {
				const rawSections = Array.isArray(p.sections) ? p.sections : null
				if (!rawSections) {
					return { ok: false, reason: 'sections_must_be_array' }
				}

				const removedAgentIds = existingOrder.filter((id) => {
					const entry = data[id]
					const comp = entry && entry.component
						? normalizeElasticComponentType(entry.component)
						: ''
					return comp === AGENT_HTML_ELASTIC_COMPONENT_ID
				})

				const sections = rawSections.map((row) => {
					const normalized = normalizeAgentSyncSectionRow(row)
					if (!normalized || !normalized.id) {
						return normalized
					}
					const resolved = {
						...normalized,
						id: resolveAgentSectionSlug(normalized.id, removedAgentIds),
					}
					if (normalized.afterSectionId != null && String(normalized.afterSectionId).trim() !== '') {
						resolved.afterSectionId = resolveAgentSectionSlug(
							normalized.afterSectionId,
							removedAgentIds,
						)
					}
					return resolved
				})

				const incomingIds = sections.map(s => (s && s.id != null ? String(s.id).trim() : ''))
				if (new Set(incomingIds).size !== incomingIds.length) {
					return { ok: false, reason: 'duplicate_section_id' }
				}

				for (let i = 0; i < incomingIds.length; i += 1) {
					const incomingId = incomingIds[i]
					if (!incomingId) {
						// eslint-disable-next-line no-continue
						continue
					}
					if (ELASTIC_SLUG_REGEXP.test(incomingId)) {
						// eslint-disable-next-line no-continue
						continue
					}
					const isExistingAgentId = removedAgentIds.some(existingId => String(existingId) === incomingId)
					if (!isExistingAgentId) {
						return { ok: false, reason: 'invalid_section_slug', id: incomingId }
					}
				}

				const replaceAll = p.sectionsReplaceAll === true
				let sectionsToApply = sections
				if (sections.length > 0) {
					sectionsToApply = replaceAll
						? [...sections]
						: this.mergeAgentHtmlSyncSections(sections, removedAgentIds, data)
				}

				const placementHints = sections.filter(row => row && typeof row === 'object' && row.afterSectionId != null
					&& String(row.afterSectionId).trim() !== '')
				if (sections.length > 0) {
					sectionsToApply = this.applyAgentSectionAfterPlacements(sectionsToApply, placementHints)
				}

				sectionsToApply = sectionsToApply.map(row => this.enrichAgentSectionSyncRow(row, data))

				const ids = sectionsToApply.map(s => (s && s.id != null ? String(s.id).trim() : ''))
				if (new Set(ids).size !== ids.length) {
					return { ok: false, reason: 'duplicate_section_id' }
				}

				for (let i = 0; i < ids.length; i += 1) {
					const sectionId = ids[i]
					if (!sectionId) {
						// eslint-disable-next-line no-continue
						continue
					}
					if (ELASTIC_SLUG_REGEXP.test(sectionId)) {
						// eslint-disable-next-line no-continue
						continue
					}
					const isExistingAgentId = removedAgentIds.some(existingId => String(existingId) === sectionId)
					if (!isExistingAgentId) {
						return { ok: false, reason: 'invalid_section_slug', id: sectionId }
					}
				}

				const newAgentIds = [...ids]
				let validatedAgentOrder = newAgentIds
				if (hasAgentSectionsOrder) {
					const parsed = this.parseAgentSectionsOrderArg(p.agentSectionsOrder)
					if (!parsed.ok) {
						return parsed
					}
					const { order: desiredOrder } = parsed
					if (desiredOrder.length !== newAgentIds.length) {
						return { ok: false, reason: 'agent_sections_order_length_mismatch' }
					}
					const setN = new Set(newAgentIds)
					for (let oi = 0; oi < desiredOrder.length; oi += 1) {
						if (!setN.has(desiredOrder[oi])) {
							return { ok: false, reason: 'agent_sections_order_unknown_id', id: desiredOrder[oi] }
						}
					}
					validatedAgentOrder = desiredOrder
				}

				for (let r = 0; r < removedAgentIds.length; r += 1) {
					const oldId = removedAgentIds[r]
					if (!newAgentIds.includes(oldId)) {
						this.scrubAgentHtmlStringsForSection(oldId)
						this.scrubAgentHtmlImagesForSection(oldId)
						delete data[oldId]
					}
				}

				body.elastic = [...nonAgentIds, ...validatedAgentOrder]

				let touchedAgentStrings = false
				for (let s = 0; s < sectionsToApply.length; s += 1) {
					const row = sectionsToApply[s] || {}
					const id = String(row.id || '').trim()
					const componentData = this.initPageDataConfig(id)
					componentData.component = AGENT_HTML_ELASTIC_COMPONENT_ID
					if (Object.prototype.hasOwnProperty.call(row, 'html')) {
						componentData.html = row.html != null
							? normalizeAgentHtmlHrefPlaceholders(String(row.html))
							: ''
					}

					if (Object.prototype.hasOwnProperty.call(row, 'agentCss')) {
						if (row.agentCss === null) {
							delete componentData.agentCss
						} else {
							componentData.agentCss = row.agentCss != null ? String(row.agentCss) : ''
						}
					}

					const hasI18nKey = Object.prototype.hasOwnProperty.call(row, 'i18n')
					if (hasI18nKey) {
						// Explicit null clears i18n + strings for this section only.
						if (row.i18n === null) {
							this.scrubAgentHtmlStringsForSection(id)
							delete componentData.i18n
							touchedAgentStrings = true
						} else {
							const nI18n = normalizeAgentI18nObject(row.i18n)
							// Empty/invalid payload must not wipe existing copy (partial updates are merged).
							if (!nI18n) {
								// no-op
							} else {
								const fromStrings = collectAgentSectionI18nFromPageStrings(
									this.page.strings,
									this.locales,
									id,
								)
								const cachedI18n = componentData.i18n && typeof componentData.i18n === 'object'
									&& !Array.isArray(componentData.i18n)
									? componentData.i18n
									: null
								const existingI18n = mergeAgentI18nObjects(cachedI18n, fromStrings)
								const merged = mergeAgentI18nObjects(existingI18n, nI18n)
								componentData.i18n = cloneDeep(merged)
								this.scrubAgentHtmlStringsForSection(id)
								this.applyAgentSectionI18nToPageStrings(id, merged)
								touchedAgentStrings = true
							}
						}
					}

					if (componentData.attrHeading === undefined) {
						componentData.attrHeading = 'none'
					}
					if (componentData.attrAlign === undefined) {
						componentData.attrAlign = 'left'
					}
					if (componentData.attrBackground === undefined) {
						componentData.attrBackground = 'white'
					}
				}

				agentSectionIds = [...validatedAgentOrder]
				elasticOrderOut = [...body.elastic]

				if (this.socket) {
					this.socket.emit('action', 'putData', this.page.data)
					if (touchedAgentStrings) {
						this.socket.emit('action', 'putLocales', this.page.strings)
					}
				}
				didDataOrStrings = true
			} else if (hasAgentSectionsOrder) {
				const ordRes = validateAndApplyAgentSectionsOrder(p.agentSectionsOrder)
				if (!ordRes.ok) {
					return ordRes
				}
				if (this.socket) {
					this.socket.emit('action', 'putData', this.page.data)
				}
				didDataOrStrings = true
			}

			if (hasStylesheet) {
				this.page.stylesheet = p.stylesheet != null ? String(p.stylesheet) : ''
				if (this.debouncedStylesheetEmit) {
					this.debouncedStylesheetEmit(this.page.stylesheet)
				}
			}

			if (didDataOrStrings || hasStylesheet) {
				this.afterUpdate()
			}

			return {
				ok: true,
				elasticSectionOrder: elasticOrderOut,
				agentSectionIds,
			}
		},
	},
	beforeDestroy() {
		// Cancel any pending debounced stylesheet emits
		if (this.debouncedStylesheetEmit) {
			this.debouncedStylesheetEmit.cancel()
		}
		// Destroy socket connection before exiting the page to prevent a memory leak
		if (this.socket) {
			this.socket.disconnect()
		}
	},
}
</script>

<style scoped>
	.editor-webpage {
		overflow: hidden;
	}

	.topbar {
		position: relative;
		top:0;
		left:0;
		right:0;
		width: 100%;
		background: white;
		border-bottom: 1px solid #aaa;
	}

	.topbar-divider {
		border: 1px solid #f1f1f1;
    width: 0;
    height: 26px;
    display: inline-flex;
    flex-direction: column;
    margin-top: 9px;
    margin-bottom: -9px;
	}

	.topbar-state {
		color: gray;
		font-size: 14px;
	}

	.previewbar {
		position: absolute;
		top:-35px;
		left: 50%;
		margin-left: -150px;
		width: 300px;
		background: white;
		border-bottom: 1px solid #aaa;
		border-left: 1px solid #aaa;
		border-right: 1px solid #aaa;

		transition: 150ms top;
		transition-delay: 500ms;
	}

	.previewbar:hover {
		top: 0;
		transition-delay: 150ms;
	}

	.page-frame {
		width: calc(100vw - 80px);
		height: calc(100vh - 55px);
		margin-left: 80px;
		border: none;
		transition: margin-left 0.3s ease, width 0.3s ease;
	}

	.page-frame.leftbar-open:not(.tablet):not(.mobile) {
		width: calc(100vw - 480px);
		margin-left: 480px;
	}

	.page-frame.preview {
		width: 100vw;
		height: calc(100vh - 8px);
		margin-left: 0;
	}

	.page-frame.tablet {
		position: relative;
		left: 50%;
		margin-left: -300px;
		margin-top: 60px;
		max-height: calc(100vh - 97px - 60px);
		width: 600px;
		height: 906px;
		border: 8px solid black;
	}

	.page-frame.mobile {
		position: relative;
		left: 50%;
		margin-left: -160px;
		margin-top: 60px;
		max-height: calc(100vh - 97px - 60px);
		width: 320px;
		height: 480px;
		border: 8px solid black;
	}

	.tags-section {
		padding: 20px;
		margin-top: 10px;
		border: 1px solid #f1f1f1;
	}

	.css-editor >>> .code-editor-wrapper .CodeMirror {
		min-height: 400px;
	}

	.css-editor >>> .code-editor-wrapper .CodeMirror-scroll {
		min-height: 400px;
		max-height: 800px;
	}
</style>
